import React, { useState } from 'react';
import { TextField, InputAdornment, IconButton, Popover } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

export const toYyyyMmDd = (v: string): string | null => {
  const s = String(v || '').trim();
  if (s.includes('/')) {
    const p = s.split('/').map((x) => parseInt(x, 10));
    if (p.length === 3 && !p.some(isNaN) && p[2] >= 1000) {
      return `${p[2]}-${String(p[0]).padStart(2, '0')}-${String(p[1]).padStart(2, '0')}`;
    }
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
};

export const isDateOnOrBeforeToday = (value: string): boolean => {
  const y = toYyyyMmDd(value) ?? value;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(y)) return true;
  const t = new Date();
  const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  return y <= today;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  label: string;
  error?: boolean;
  helperText?: string;
  maxDate?: Date;
};

export const DateFieldWithCalendar: React.FC<Props> = ({
  value,
  onChange,
  label,
  error = false,
  helperText,
  maxDate = new Date(),
}) => {
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);

  const norm = value ? toYyyyMmDd(value) || value : '';
  const display = norm && /^\d{4}-\d{2}-\d{2}$/.test(norm)
    ? `${norm.slice(5, 7)}/${norm.slice(8, 10)}/${norm.slice(0, 4)}`
    : (value || '');
  const calVal = norm && /^\d{4}-\d{2}-\d{2}$/.test(norm)
    ? new Date(parseInt(norm.slice(0, 4), 10), parseInt(norm.slice(5, 7), 10) - 1, parseInt(norm.slice(8, 10), 10))
    : null;

  const setVal = (val: string) => onChange(val);

  return (
    <>
      <TextField
        value={display}
        onChange={(e) => setVal(toYyyyMmDd(e.target.value) ?? e.target.value)}
        onBlur={(e) => {
          const v = (e.target as HTMLInputElement).value.trim();
          if (v) setVal(toYyyyMmDd(v) ?? v);
        }}
        label={label}
        placeholder="MM/DD/YYYY"
        fullWidth
        error={error}
        helperText={helperText}
        InputLabelProps={{ shrink: !!display }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label="Calendar" onClick={(e) => setCalendarAnchor(e.currentTarget)} edge="end">
                <CalendarMonthIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Popover
          open={!!calendarAnchor}
          anchorEl={calendarAnchor}
          onClose={() => setCalendarAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <DateCalendar
            value={calVal}
            onChange={(d) => {
              if (d) {
                setVal(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
                setCalendarAnchor(null);
              }
            }}
            maxDate={maxDate}
          />
        </Popover>
      </LocalizationProvider>
    </>
  );
};
